/**
 * Helpers for paths that point inside multi-entry archives.
 *
 * The c2-discover-api worker uses the boundary marker `!/` to identify entries
 * inside archives (zip / jar / war / ear). A virtual path like
 *
 *   /data/exports/foo.zip!/orders/jan.csv
 *
 * names the entry `orders/jan.csv` inside `foo.zip` on disk at `/data/exports/`.
 * S3 and Azure Blob URLs use the same shape: `s3://bucket/q1.zip!/users.csv`.
 *
 * The boundary survives every layer in the platform — the worker's archive
 * router uses it to materialise + walk the archive, the platform-api passes it
 * through byte-for-byte, and the UI receives it on:
 *
 *   - `OverviewResponse.tables[].name`         (typed file runs; the table
 *                                                name is the relative file path
 *                                                so any segment containing
 *                                                `!/` indicates an archived entry)
 *   - `OverviewResponse.scan_meta.candidate_sources[].folder_path`
 *   - data-source `config.rootPath`            (when the source itself was
 *                                                created against a virtual path)
 *
 * Anywhere the UI displays a path or table name and wants to mark archive
 * findings, use {@link isArchivePath} to check and {@link splitArchivePath}
 * to render the outer + inner halves.
 */

/** Boundary marker between the on-disk archive and the entry path inside it. */
export const ARCHIVE_BOUNDARY = '!/';

/** True when the value is a string containing the archive boundary marker. */
export function isArchivePath(value) {
  return typeof value === 'string' && value.indexOf(ARCHIVE_BOUNDARY) >= 0;
}

/**
 * Split a virtual archive path into its outer and inner halves.
 *
 * For non-archive paths returns the original string as `outer` and an empty
 * string as `inner` — callers can branch on `inner` being non-empty without
 * a separate {@link isArchivePath} check.
 */
export function splitArchivePath(value) {
  if (!isArchivePath(value)) return { outer: value || '', inner: '' };
  const i = value.indexOf(ARCHIVE_BOUNDARY);
  return { outer: value.slice(0, i), inner: value.slice(i + ARCHIVE_BOUNDARY.length) };
}

/**
 * Leaf name of the on-disk archive — `foo.zip` for `/data/foo.zip!/orders/`.
 * Returns the input unchanged when the value is not a virtual archive path.
 */
export function archiveOuterName(value) {
  const { outer } = splitArchivePath(value);
  if (!outer) return value || '';
  const slash = Math.max(outer.lastIndexOf('/'), outer.lastIndexOf('\\'));
  return slash >= 0 && slash < outer.length - 1 ? outer.slice(slash + 1) : outer;
}

/**
 * Path inside the archive — `orders/jan.csv` for `/data/foo.zip!/orders/jan.csv`,
 * an empty string for non-archive paths or for the bare archive root.
 */
export function archiveInnerPath(value) {
  if (!isArchivePath(value)) return '';
  const { inner } = splitArchivePath(value);
  return inner;
}

/**
 * Compact single-line label of the form `foo.zip / orders/jan.csv` for archive
 * paths, or the input unchanged for non-archive paths. Suitable for breadcrumbs
 * and tight tooltips where rendering structured spans is too heavy.
 */
export function compactArchiveLabel(value) {
  if (!isArchivePath(value)) return value || '';
  const inner = archiveInnerPath(value);
  return inner ? `${archiveOuterName(value)} / ${inner}` : `${archiveOuterName(value)} (archive root)`;
}
